import DOMPurify from 'dompurify';

// iframe embeds are a legitimate newsletter/blog feature (video embeds), but an
// arbitrary iframe src is a phishing/clickjacking vector. Restrict iframe src to
// known video hosts — mirrors the backend sanitizeRichHtml allowedIframeHostnames.
const ALLOWED_IFRAME_HOSTS = new Set([
  'www.youtube.com',
  'youtube.com',
  'www.youtube-nocookie.com',
  'youtube-nocookie.com',
  'player.vimeo.com',
  'vimeo.com',
]);

// Installed once at module load (not lazily) so the hook is deterministic and
// independent of which sanitize() call runs first. The hook is process-global but
// only ever REMOVES a non-allowlisted iframe — strictly safer for every caller,
// never more permissive.
DOMPurify.addHook('uponSanitizeElement', (node, data) => {
  if (data.tagName !== 'iframe') return;
  const el = node as Element;
  const src = el.getAttribute('src') || '';
  let host = '';
  try {
    host = new URL(src, window.location.origin).hostname;
  } catch {
    host = '';
  }
  if (!ALLOWED_IFRAME_HOSTS.has(host)) {
    el.parentNode?.removeChild(el);
  }
});

/**
 * Sanitize HTML content for safe rendering via dangerouslySetInnerHTML.
 * Defense-in-depth: backend already sanitizes, this provides a second layer.
 */
export function sanitizeHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'u', 'a', 'img',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'blockquote', 'pre', 'code',
      'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'div', 'span', 'hr', 'sub', 'sup', 'del',
      'figure', 'figcaption', 'video', 'source', 'iframe',
    ],
    ALLOWED_ATTR: [
      'href', 'src', 'alt', 'title', 'class', 'style',
      'target', 'rel', 'width', 'height', 'id',
      'colspan', 'rowspan', 'controls', 'type',
      'allowfullscreen', 'frameborder',
    ],
    ALLOW_DATA_ATTR: false,
  });
}

/**
 * Reverse the server's `escapeHtml` for display and editing.
 *
 * ⚠️ WHY THIS IS NEEDED. Text fields like a permission slip body are stored
 * ESCAPED (the backend applies `escapeHtml` on both create and update), so the
 * database holds `Meet at 9am &amp; bring a hat`. Two things go wrong without
 * this:
 *
 *   1. Rendering it as a React text child shows the parent the raw entities —
 *      `&amp;`, and `&#x2F;` for every slash in a date or URL — on a document
 *      they are being asked to consent to.
 *   2. Loading it into an edit form and saving escapes it AGAIN, compounding on
 *      every edit: `&amp;` → `&amp;amp;` → `&amp;amp;amp;`. That is unbounded
 *      data corruption, not a display quirk.
 *
 * The entity set MUST match the backend's `escapeHtml` (& < > " ' ` = /) —
 * note it escapes `/` and `=`, which most implementations do not.
 */
const HTML_ENTITY_MAP: Record<string, string> = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#x27;': "'",
  '&#x60;': '`',
  '&#x3D;': '=',
  '&#x2F;': '/',
};

export function unescapeHtml(input: string | null | undefined): string {
  if (!input) return '';
  // `&amp;` LAST would double-decode (`&amp;lt;` → `<` instead of `&lt;`), so
  // decode the named entities first and ampersand at the end.
  return input
    .replace(/&lt;|&gt;|&quot;|&#x27;|&#x60;|&#x3D;|&#x2F;/g, (m) => HTML_ENTITY_MAP[m] ?? m)
    .replace(/&amp;/g, '&');
}
